import {
  ArrowRightOutlined,
  CalendarOutlined,
  FileTextOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateTenderRequest } from "../../../api/tender";
import { useAuth } from "../../../hooks/useAuth";
import { useCreateTender } from "../../../hooks/useTenders";
import { COLORS, REGIONS } from "../../../utils/constants";

const { Title, Text } = Typography;

// Basic tender information form data
export interface BasicTenderFormData {
  tenderName: string;
  clientName: string;
  siteAddress?: string;
  region: CreateTenderRequest["region"];
}

interface TenderFormProps {
  userRoleData?: any;
  isImportedData?: boolean;
  importedOpportunity?: any;
  onCreateNew?: () => void;
}

const TenderForm: React.FC<TenderFormProps> = ({
  userRoleData,
  isImportedData = false,
  importedOpportunity,
  onCreateNew,
}) => {
  const [form] = Form.useForm<BasicTenderFormData>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const createTenderMutation = useCreateTender();

  const userData = userRoleData?.data;

  // Track date created and date modified
  const [dateCreated] = useState(new Date());
  const [dateModified, setDateModified] = useState(new Date());
  const [showDates, setShowDates] = useState(false);

  // Update dateModified when form values change
  const handleFieldsChange = () => {
    setDateModified(new Date());

    // Check if all required fields are filled
    const values = form.getFieldsValue();
    const allRequiredFieldsFilled: any =
      values.tenderName &&
      values.tenderName.trim() !== "" &&
      values.clientName &&
      values.clientName.trim() !== "" &&
      values.region &&
      values.region.trim() !== "";

    setShowDates(allRequiredFieldsFilled);
  };

  // Set default region when user data is loaded
  useEffect(() => {
    if (
      userData &&
      !Array.isArray(userData) &&
      userData.Region &&
      userData.Region !== "All Regions"
    ) {
      form.setFieldsValue({
        region: userData.Region as CreateTenderRequest["region"],
      });
    }
  }, [userData, form]);

  const handleSaveTender = async (
    data: BasicTenderFormData,
    navigateTo: string
  ) => {
    try {

      message.loading({ content: "Creating tender...", key: "createTender", duration: 0 });

      const modifiedBy =
        (userData && !Array.isArray(userData)
          ? userData.FullName
          : undefined) ||
        user?.name ||
        user?.email ||
        "Unknown User";

      const response = await createTenderMutation.mutateAsync({
        tenderName: data.tenderName,
        clientName: data.clientName,
        siteAddress: data.siteAddress || "",
        region: data.region,
        createdBy: modifiedBy,

        status: "In-progress",
        tenderCreatedDate: dateCreated.toISOString(),
        tenderModifiedDate: new Date().toISOString(),
        tenderModifiedBy: modifiedBy,
      });

      // Save tenderId to localStorage if available
      if (response?.data?.tenderId) {
        localStorage.setItem("currentTenderId", response.data.tenderId);
      }

      message.success({
        content: "Tender created successfully",
        key: "createTender",
        duration: 3,
      });

      navigate(navigateTo);
    } catch (error: any) {
      console.error("❌ Error creating tender:", error);
      message.error({
        content: `Failed to create tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "createTender",
        duration: 5,
      });
    }
  };

  const onSubmit = (data: BasicTenderFormData) => {
    handleSaveTender(data, "/job-types");
  };

  const onSaveAndExit = async () => {
    const values = await form.validateFields();
    handleSaveTender(values, "/");
  };

  // Helper component for displaying imported data fields
  const InfoField: React.FC<{
    label: string;
    value: string;
    required?: boolean;
  }> = ({ label, value, required }) => (
    <div style={{ marginBottom: "12px" }}>
      <Text
        className="block"
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#374151",
          marginBottom: "6px",
          display: "block",
        }}
      >
        {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
      </Text>
      <Text
        style={{
          fontSize: "14px",
          color: "#111827",
          display: "block",
        }}
      >
        {value || "-"}
      </Text>
    </div>
  );

  // If imported data, show imported opportunity display
  if (isImportedData && importedOpportunity) {
    return (
      <Card
        style={{
          borderRadius: "8px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <Space align="center" style={{ marginBottom: "8px" }}>
            <FileTextOutlined
              style={{ fontSize: "20px", color: COLORS.primary }}
            />
            <Title level={5} style={{ margin: 0, color: COLORS.primary }}>
              Imported Opportunity Data
            </Title>
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: "13px" }}>
            The following information was imported from the selected
            opportunity.
          </Text>
        </div>

        {importedOpportunity.tenderId && (
          <>
            <div style={{ marginBottom: "16px" }}>
              <Text style={{ fontSize: "13px", color: "#6b7280" }}>
                <strong>Opportunity ID:</strong> {importedOpportunity.tenderId}
              </Text>
            </div>
            <Divider style={{ margin: "16px 0" }} />
          </>
        )}

        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <InfoField
              label="Tender Name"
              value={importedOpportunity.tender}
              required
            />
          </Col>

          <Col xs={24} md={12}>
            <InfoField
              label="Client Name"
              value={importedOpportunity.client}
              required
            />
          </Col>

          <Col xs={24} md={12}>
            <InfoField
              label="Site Address"
              value={importedOpportunity.siteAddress || ""}
            />
          </Col>

          <Col xs={24} md={12}>
            <InfoField
              label="Region"
              value={importedOpportunity.region}
              required
            />
          </Col>
        </Row>

        {/* {onCreateNew && (
          <>
            <Divider
              style={{ margin: "24px 0", fontSize: "14px", color: "#6b7280" }}
            >
              or
            </Divider>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onCreateNew}
                size="large"
                style={{
                  backgroundColor: COLORS.primary,
                  borderColor: COLORS.primary,
                }}
              >
                Create New Tender
              </Button>
            </div>
          </>
        )} */}

        {/* Action Buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "24px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              padding: "16px 0",
            }}
          >
            <Button
              onClick={async () => {
                try {

                  message.loading({
                    content: "Creating tender...",
                    key: "createTender",
                    duration: 0,
                  });

                  const modifiedBy =
                    (userData && !Array.isArray(userData)
                      ? userData.FullName
                      : undefined) ||
                    user?.name ||
                    user?.email ||
                    "Unknown User";

                  const response = await createTenderMutation.mutateAsync({
                    tenderName: importedOpportunity.tender,
                    clientName: importedOpportunity.client,
                    siteAddress: importedOpportunity.siteAddress || "",
                    region: importedOpportunity.region,
                    createdBy: modifiedBy,
            
                    status: "In-progress",
                    tenderCreatedDate: new Date().toISOString(),
                    tenderModifiedDate: new Date().toISOString(),
                    tenderModifiedBy: modifiedBy,
                    opportunityId: importedOpportunity.tenderId, // BC Opportunity No
                  });

                  if (response?.data?.tenderId) {
                    localStorage.setItem(
                      "currentTenderId",
                      response.data.tenderId
                    );
                  }

                  message.success({
                    content: "Tender created successfully",
                    key: "createTender",
                    duration: 3,
                  });

                  navigate("/");
                } catch (error: any) {
                  console.error("❌ Error creating tender:", error);
                  message.error({
                    content: `Failed to create tender: ${
                      error.response?.data?.message ||
                      error.message ||
                      "Unknown error"
                    }`,
                    key: "createTender",
                    duration: 5,
                  });
                }
              }}
              disabled={createTenderMutation.isPending}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                try {

                  message.loading({
                    content: "Creating tender...",
                    key: "createTender",
                    duration: 0,
                  });

                  const modifiedBy =
                    (userData && !Array.isArray(userData)
                      ? userData.FullName
                      : undefined) ||
                    user?.name ||
                    user?.email ||
                    "Unknown User";

                  const response = await createTenderMutation.mutateAsync({
                    tenderName: importedOpportunity.tender,
                    clientName: importedOpportunity.client,
                    siteAddress: importedOpportunity.siteAddress || "",
                    region: importedOpportunity.region,
                    createdBy: modifiedBy,
            
                    status: "In-progress",
                    tenderCreatedDate: new Date().toISOString(),
                    tenderModifiedDate: new Date().toISOString(),
                    tenderModifiedBy: modifiedBy,
                    opportunityId: importedOpportunity.tenderId, // BC Opportunity No
                  });

                  if (response?.data?.tenderId) {
                    localStorage.setItem(
                      "currentTenderId",
                      response.data.tenderId
                    );
                  }

                  message.success({
                    content: "Tender created successfully",
                    key: "createTender",
                    duration: 3,
                  });

                  navigate("/job-types");
                } catch (error: any) {
                  console.error("❌ Error creating tender:", error);
                  message.error({
                    content: `Failed to create tender: ${
                      error.response?.data?.message ||
                      error.message ||
                      "Unknown error"
                    }`,
                    key: "createTender",
                    duration: 5,
                  });
                }
              }}
              loading={createTenderMutation.isPending}
              disabled={createTenderMutation.isPending}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Regular form
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      onFieldsChange={handleFieldsChange}
    >
      <div className="mb-6">
        <Title
          level={5}
          className="mb-4"
          style={{
            fontWeight: 600,
            fontSize: "16px",
            color: "#111827",
          }}
        >
          Create a new tender
        </Title>

        <Row gutter={[24, 24]}>
          {/* Tender Name */}
          <Col xs={24} md={12}>
            <Form.Item
              label={
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                  }}
                >
                  Tender Name
                </Text>
              }
              name="tenderName"
              rules={[{ required: true, message: "Please enter tender name" }]}
            >
              <Input placeholder="Enter tender name" />
            </Form.Item>
          </Col>

          {/* Client Name */}
          <Col xs={24} md={12}>
            <Form.Item
              label={
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                  }}
                >
                  Client Name
                </Text>
              }
              name="clientName"
              rules={[{ required: true, message: "Please enter client name" }]}
            >
              <Input placeholder="Enter client name" />
            </Form.Item>
          </Col>

          {/* Site Address */}
          <Col xs={24} md={12}>
            <Form.Item
              label={
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                  }}
                >
                  Site Address
                </Text>
              }
              name="siteAddress"
            >
              <Input placeholder="Enter site address" />
            </Form.Item>
          </Col>

          {/* Region */}
          <Col xs={24} md={12}>
            <Form.Item
              label={
                <Text
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                  }}
                >
                  Region
                </Text>
              }
              name="region"
              rules={[{ required: true, message: "Please select region" }]}
              help={
                userData &&
                !Array.isArray(userData) &&
                userData.Region &&
                userData.Region !== "All Regions"
                  ? `Region auto-filled based on your profile (${userData.RoleDisplayName})`
                  : undefined
              }
            >
              <Select
                placeholder="Select region"
                disabled={
                  !!(
                    userData &&
                    !Array.isArray(userData) &&
                    userData.Region &&
                    userData.Region !== "All Regions"
                  )
                }
                options={REGIONS.map((region) => ({
                  label: region,
                  value: region,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Date Information - only show if all required fields are filled */}
      {showDates && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <CalendarOutlined style={{ color: "#6b7280", fontSize: "14px" }} />
            <Text style={{ color: "#6b7280", fontSize: "14px" }}>
              Date Created:{" "}
              <span style={{ fontWeight: 600, color: "#374151" }}>
                {format(dateCreated, "MMMM do, yyyy h:mm a")}
              </span>
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <CalendarOutlined style={{ color: "#6b7280", fontSize: "14px" }} />
            <Text style={{ color: "#6b7280", fontSize: "14px" }}>
              Last Modified:{" "}
              <span style={{ fontWeight: 600, color: "#374151" }}>
                {format(dateModified, "MMMM do, yyyy h:mm a")}
              </span>
            </Text>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "24px" }}>
        <div className="px-6 py-4 flex justify-end gap-3">
          <Button
            onClick={onSaveAndExit}
            disabled={createTenderMutation.isPending}
            style={{
              borderColor: COLORS.primary,
              color: COLORS.primary,
            }}
          >
            Save and Exit
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={createTenderMutation.isPending}
            loading={createTenderMutation.isPending}
            style={{
              backgroundColor: COLORS.primary,
              borderColor: COLORS.primary,
            }}
          >
            Save & Continue <ArrowRightOutlined />
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default TenderForm;
